import Vue, { AsyncComponent, CreateElement, RenderContext, VNode } from "vue";
import Vuex, { Module, Store } from "vuex";
import VueRouter, { RouteConfig } from "vue-router";
import VueI18n, { Values, TranslateResult, LocaleMessageObject } from "vue-i18n";
import { HTTPClient, AxiosResponse } from "../HTTP";
import { VueConstructor } from "vue";
import toast, { ToastFunction, ToastOptions } from "./Toasted";
import { embedGTag } from "./GTag";

Vue.use(VueI18n);
Vue.use(Vuex);
Vue.use(VueRouter);

const I18N_API = "/i18n";
const ALLOWED_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_".split("");
const RESERVED_NAMES = ["i18n", "toast", "invokeBeforeEnterAnyRoute"];

export interface SubMenuItem {
    readonly id: string;
    readonly descrKey: string;
    readonly icon: string;
    readonly isVisible: boolean;
    onClick: () => void;
}

export interface MenuItem extends SubMenuItem {
    isOpen?: boolean;
    children?: SubMenuItem[];
}

export class AbacoVuexModule<S = any, R = any> implements Module<S, R> {
    protected store?: Store<unknown>;
    namespaced = true;

    setStore(store: Store<unknown>) {
        this.store = store;
    }
}

export interface NavBarFilterObj {
    component: string;
    props: { [key: string]: any }
}

export type NavBarFilter = NavBarFilterObj | string

export interface ComponentMetadata {
    i18nDescrKey: string;
    tag?: string;
    pinnable?: boolean;
    filters?: NavBarFilter[];
}

export interface ComponentMetadataWithId extends ComponentMetadata {
    id: string;
}

export interface AsyncComponentWithMetadata {
    meta: ComponentMetadata;
    component: AsyncComponent;
}

export type EnvConfig = {[key: string]: any};

export type GtagFunction = (...args: any) => void;
export type SetI18nVariantFunction = (variant: string | I18nVariant | null) => void;

export type VueRegistration = {
    [key: string]: AsyncComponent | AsyncComponentWithMetadata;
};

export type GlobalComponentsMetadata = {
    [key: string]: ComponentMetadataWithId[];
};

export interface ApplicationReadyEvent {
    http: HTTPClient;
    vue: Vue;
    modules: AbacoWebModule[];
    globalComponentsMetadata: GlobalComponentsMetadata;
    envConfig: EnvConfig;
    gtag: GtagFunction;
    setI18nVariant: SetI18nVariantFunction;
}

export interface RouterReadyEvent {
    http: HTTPClient;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    store: Store<any>;
    router: VueRouter;
}

export interface AbacoWebModule {
    id: string;
    globalComponents?: VueRegistration;
    vuexModule?: AbacoVuexModule;
    routes?: RouteConfig[];
    forceTopLevelRoutes?: boolean;
    menu?: MenuItem[];
    additionalI18N?: string[];
    dependsOn?: { [key: string]: null | (() => AbacoWebModule) };
    notifyAnyRoutes?: boolean;
    onRouterReady?: (event: RouterReadyEvent) => void;
    onApplicationReady?: (event: ApplicationReadyEvent) => void;
    beforeEnterAnyRoutes?: (http: HTTPClient) => Promise<void>;
}

export interface I18nVariant {
    modules: string[];
    variant: string;
}

type VueRenderFunction<P> = (createElement: CreateElement, hack: RenderContext<P>) => VNode;

export interface ApplicationConfig {
    http: HTTPClient;
    enableGtag?: boolean;
    i18nAPI?: string | null;
    i18nVariant?: I18nVariant;
    base?: string;
    routerMode?: "history" | "hash";
    rootComponent?: VueConstructor<Vue>;
    toastFunction?: ToastFunction;
}

type ModulesMap = { [key: string]: AbacoWebModule };

function isAsyncComponentWithMetadata(val: any): val is AsyncComponentWithMetadata {
    return val && val.meta;
}

export class Application {
    private config: ApplicationConfig;
    private envConfig: EnvConfig = {};
    private modules: AbacoWebModule[] = [];
    private i18n?: VueI18n;
    private applicationReadyResolver: any;
    private applicationReadyPromise: Promise<void>;
    private gtag?: GtagFunction;

    constructor(config: ApplicationConfig) {
        this.config = config;
        if (config.i18nAPI !== null) {
            this.i18n = new VueI18n({
                locale: navigator.language,
                // fallbackLocale: "en",
                messages: {}
            });
        }
        this.applicationReadyPromise = new Promise((resolve, reject) => {
            this.applicationReadyResolver = resolve;
        });
    }

    public get http(): HTTPClient {
        return this.config.http;
    }

    public add(module: AbacoWebModule): Application {
        const te = (s: string) => {
            throw new Error(`Module id ${s}`);
        };
        if (module.id.length === 0) {
            te("cannot be empty");
        }
        if (RESERVED_NAMES.includes(module.id)) {
            te(`${module.id} is a reserved word`);
        }
        if (!isNaN(+module.id.charAt(0))) {
            te("cannot start with a digit");
        }
        for (let i = 0; i < module.id.length; i++) {
            if (!ALLOWED_CHARS.includes(module.id.charAt(i))) {
                te("contains not allowed chars");
            }
        }

        this.modules.push(module);
        return this;
    }

    public async start<P>(render?: VueRenderFunction<P>): Promise<Vue> {
        try {
            const baseUrl = process.env.BASE_URL || '/portal/';
            const baseUrlWithSlash = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
            const configUrl = `${baseUrlWithSlash}general-config.json`;

            const response = await this.config.http.get(configUrl);
            this.envConfig = response.data;
        } catch (error) {
            console.error("cannot load env config because " + error);
            this.envConfig = {};
        }
        this.gtag = () => { console.warn("[GTAG]: gtag not configured") }
        if (this.config.enableGtag) {
            const gtagIds = this.envConfig.gtagId || null
            if (gtagIds) {
                this.gtag = embedGTag(gtagIds);
            }
        }
        this.checkDependencies();

        if (this.i18n) {
            await this.setLanguage(this.i18n.locale);
        }
        this.registerGlobalcomponents();

        const store = this.createStore();
        const router = this.createRouter(store);

        const modules = this.modules.reduce((accum, m) => {
            accum[m.id] = m;
            return accum;
        }, {} as ModulesMap);

        if (this.gtag) {
            Vue.prototype.$gtag = this.gtag;
        }

        const vue = new Vue({
            i18n: this.i18n,
            router,
            store,
            render,
            provide: {
                ...modules,
                envConfig: this.envConfig,
                i18n: (key: string, values?: Values): TranslateResult => {
                    return this.i18n ? this.i18n.t(key, values) : key;
                },
                toast: (message: string, options: ToastOptions) => {
                    return this.config.toastFunction 
                        ? this.config.toastFunction(
                            message, 
                            options, 
                            (key: string, values?: Values): TranslateResult => this.i18n ? this.i18n.t(key, values) : key
                        )
                        : toast(message, options);
                },
                invokeBeforeEnterAnyRoute: (mods: AbacoWebModule[]) => this.invokeBeforeEnterAnyRoute(mods),
                gtag: this.gtag,
                setI18nVariant: (variant: string | I18nVariant | null) => this.setI18nVariant(variant)
            }
        });

        const evt = {
            vue,
            http: this.http,
            modules: this.modules,
            globalComponentsMetadata: this.getGlobalComponentsMetadata(),
            envConfig: this.envConfig,
            gtag: this.gtag,
            setI18nVariant: (variant: string | I18nVariant | null) => this.setI18nVariant(variant)
        };
        this.modules.forEach(m => {
            if (m.onApplicationReady) {
                m.onApplicationReady(evt);
            }
        });
        this.applicationReadyResolver(null);

        return vue;
    }

    public async setLanguage(lang: string): Promise<boolean> {
        if (!this.i18n) {
            return false;
        }
        const lng = lang.replace("_", "-");

        const messages = await this.downloadMessages(lng);
        this.doSetLanguage(lng, messages || {});
        return messages !== null;
    }

    public async setI18nVariant(variant: I18nVariant | string | null){
        const isString = typeof variant === "string";
        if (!this.i18n) {
            throw new Error("No i18n found");
        }
 
        if(isString && !this.config.i18nVariant){
            throw new Error("Cannot assign a variant without having variant modules")
        }
        if(isString){
            this.config.i18nVariant!.variant = variant as string
        } else {
            this.config.i18nVariant = (variant || undefined) as I18nVariant | undefined;
        }
        
        const lang = this.i18n.locale
        const messages = await this.downloadMessages(lang);
        this.doSetLanguage(lang, messages || {});
    }

    private checkDependencies() {
        const toAdd: { [key: string]: () => AbacoWebModule } = {};
        this.modules.forEach(m => {
            if (m.dependsOn) {
                Object.keys(m.dependsOn).forEach(k => {
                    if (m.dependsOn![k] && !toAdd[k] && !this.modules.some(x => x.id === k)) {
                        toAdd[k] = m.dependsOn![k]!;
                    }
                });
            }
        });

        let added = false;
        Object.keys(toAdd).forEach(id => {
            this.modules.push(toAdd[id]());
            added = true;
        });

        if (added) {
            // Recursion to add any additional modules required by the ones I just added...
            this.checkDependencies();
            return;
        }

        this.modules.forEach(m => {
            if (m.dependsOn) {
                Object.keys(m.dependsOn).forEach(k => {
                    if (!this.modules.some(x => x.id === k)) {
                        throw new Error(
                            "Module " + m.id + "has a dependency on module: " + k + " that cannot be found"
                        );
                    }
                });
            }
        });
    }

    private doSetLanguage(lng: string, messages: LocaleMessageObject) {
        if (!this.i18n) {
            return;
        }
        this.http.setLanguage(lng);
        const el = document.querySelector("html");
        if (el) {
            el.setAttribute("lang", lng);
        }

        const oldLng = this.i18n.locale;
        this.i18n.setLocaleMessage(oldLng, {});
        this.i18n.setLocaleMessage(lng, messages);
        this.i18n.locale = lng;
    }

    // protected for testing
    protected async downloadMessages(lng: string): Promise<LocaleMessageObject | null> {
        const base = this.config.i18nAPI || I18N_API;
        const software: { [key: string]: boolean } = {};
        for (const m of this.modules) {
            software[m.id] = true;
            if (m.additionalI18N) {
                m.additionalI18N.forEach(a => {
                    software[a] = true;
                });
            }
        }

        this.config.i18nVariant?.modules.forEach(m => {
            delete software[m];
            software[m + "_" + this.config.i18nVariant?.variant] = true;
        })

        const keys =  Object.keys(software);
        // sort all variants before all the other modules to make variants wins over the other modules
        if(this.config.i18nVariant?.variant){
            keys.sort((a,b) => {
                return a.endsWith(this.config.i18nVariant!.variant) ? -1 : 1
            })
        }

        try {
            const lngWithUnderscore = lng.replace("-", "_");
            const res = await this.http.get(`${base}/translations.${lngWithUnderscore}.json`, {
                params: { software: keys, fallback: "en" }
            });
            return res.data;
        } catch (err) {
            if (!err.response) {
                throw err;
            }
            const res = err.response as AxiosResponse;
            if (res.status !== 404 && res.status !== 503) {
                throw err;
            }
            return null;
        }
    }

    private getGlobalComponentsMetadata(): GlobalComponentsMetadata {
        const ret: GlobalComponentsMetadata = {};
        for (const m of this.modules) {
            if (m.globalComponents) {
                Object.keys(m.globalComponents).forEach(c => {
                    const reg = m.globalComponents![c];
                    if (isAsyncComponentWithMetadata(reg)) {
                        const list = ret[m.id] || [];
                        list.push({ ...reg.meta, id: c });
                        ret[m.id] = list;
                    }
                });
            }
        }
        return ret;
    }

    private registerGlobalcomponents() {
        for (const m of this.modules) {
            if (m.globalComponents) {
                Object.keys(m.globalComponents).forEach(c => {
                    const reg = m.globalComponents![c];
                    if (isAsyncComponentWithMetadata(reg)) {
                        Vue.component(c, reg.component);
                    } else {
                        Vue.component(c, reg);
                    }
                });
            }
        }
    }

    private initStoreModule(xmodule: AbacoVuexModule, id: string): AbacoVuexModule {
        const abcModule = xmodule as any;
        const proto = abcModule.constructor.prototype;
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        if (proto._a_DONE) {
            throw new Error(`Cannot use AbacoVuexModule from ${id}; it's already in use`);
        }
        let vuexKey, val;
        for (const key of Object.getOwnPropertyNames(proto)) {
            if (!key.startsWith("_a_")) {
                continue;
            }

            val = proto[key];
            vuexKey = key.substring(3);

            if (!abcModule[vuexKey]) {
                abcModule[vuexKey] = val;
            } else {
                Object.assign(abcModule[vuexKey], val);
            }

            ////
            if (vuexKey === "state") {
                Object.keys(val).forEach(k => {
                    Object.defineProperty(abcModule, k, {
                        get: function () {
                            // console.log(`GET ${k}`);
                            return this.store.state[id][k];
                        },
                        set: function (val: unknown) {
                            // console.log(`SET: ${k}=${val}`);
                            this.store.commit(`${id}/set_${k}`, { [k]: val });
                        },
                        enumerable: true,
                        configurable: false
                    });
                });
            }
            ////

            delete proto[key];
        }

        /* eslint-disable-next-line @typescript-eslint/camelcase */
        proto._a_DONE = true;
        return xmodule;
    }

    private createStore(): Store<unknown> {
        const storeModules: { [key: string]: AbacoVuexModule } = {};
        for (const m of this.modules) {
            if (m.vuexModule) {
                storeModules[m.id] = this.initStoreModule(m.vuexModule, m.id);
            }
        }

        const store = new Vuex.Store({
            strict: process.env.NODE_ENV !== "production",
            modules: storeModules,
            actions: {
                reset({ commit }) {
                    // The cast to any is needed because initialState is created dybamically by the @State decorator
                    Object.keys(storeModules).forEach(m =>
                        commit(`${m}/reset_state`, (storeModules[m] as any).initialState)
                    );
                }
            }
        });

        // Set the store to any AbacoVuexModule to allow property mapping to the vuex store.
        Object.keys(storeModules).forEach(k => storeModules[k].setStore(store));

        return store;
    }

    public invokeBeforeEnterAnyRoute(whichModules: AbacoWebModule[]): Promise<unknown> {
        const called = new Set<string>();
        const allPromises: Promise<void>[] = [];

        whichModules.forEach(abacoModule => {
            const promises = this.processDependenciesOnBeforeEnterRoute(abacoModule, called);
            allPromises.push(
                Promise.all(promises).then(() => {
                    if (abacoModule.beforeEnterAnyRoutes) {
                        return abacoModule.beforeEnterAnyRoutes(this.http);
                    }
                })
            );
        });

        return Promise.all(allPromises);
    }

    private processDependenciesOnBeforeEnterRoute(abacoModule: AbacoWebModule, called: Set<string>): Promise<void>[] {
        const promises: Promise<void>[] = [];
        if (called.has(abacoModule.id)) {
            return promises;
        }

        called.add(abacoModule.id);

        if (abacoModule.dependsOn) {
            Object.keys(abacoModule.dependsOn).forEach(k => {
                const mod = this.modules.find(m => m.id === k);
                if (mod) {
                    if (mod.beforeEnterAnyRoutes) {
                        promises.push(mod.beforeEnterAnyRoutes(this.http));
                    }
                    promises.push(...this.processDependenciesOnBeforeEnterRoute(mod, called));
                }
            });
        }

        return promises;
    }

    private addBeforeEnterToRoutes(abacoModule: AbacoWebModule, notifyModules: AbacoWebModule[]): RouteConfig[] {
        if (!abacoModule.routes) {
            return [];
        }

        const myRoutes = abacoModule.routes.map(r => {
            const be = r.beforeEnter;
            const copied = { ...r };
            copied.beforeEnter = (to, from, next) => {
                this.applicationReadyPromise.then(() =>
                    this.invokeBeforeEnterAnyRoute(notifyModules).then(() => {
                        if (this.gtag) {
                            this.gtag('event', 'page_view', {
                                page_title: to.name,
                                page_path: to.path,
                                page_location: window.location.href
                            })
                        }
                        if (be) {
                            be(to, from, next);
                        } else {
                            next();
                        }
                    })
                )
            };

            return copied;
        });

        return myRoutes;
    }

    private createRouter(store: Store<unknown>): VueRouter {
        const allRoutes: RouteConfig[] = [];

        const root: RouteConfig = {
            path: "/",
            component: this.config.rootComponent
        };

        if (this.config.rootComponent) {
            allRoutes.push(root);
        }

        let top;
        const notifyAnyModules = this.modules.filter(m => m.notifyAnyRoutes);
        for (const m of this.modules) {
            if (m.routes) {
                top = m.forceTopLevelRoutes || false;

                const myRoutes = this.addBeforeEnterToRoutes(m, [m, ...notifyAnyModules.filter(x => x.id !== m.id)]);
                if (top || !this.config.rootComponent) {
                    allRoutes.push(...myRoutes);
                } else if (root.children) {
                    root.children.push(...myRoutes);
                } else {
                    root.children = [...myRoutes];
                }
            }
        }

        const router = new VueRouter({
            mode: this.config.routerMode || "history",
            base: this.config.base || process.env.BASE_URL,
            routes: allRoutes
        });

        const evt = { http: this.config.http, router, store };
        for (const m of this.modules) {
            if (m.onRouterReady) {
                m.onRouterReady(evt);
            }
        }

        return router;
    }
}
